import { authFetch } from '@/lib/api';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, MapPin, BookOpen, ShieldCheck, ClipboardCheck, ClipboardList, Monitor, Edit3, Save, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const defaultMenuItems = [
    { key: 'entreprise', to: '/guide/entreprise', icon: Building2, label: "L'entreprise", desc: 'Presentation generale de Fuji Express', color: 'bg-red-50 text-red-600 border-red-100' },
    { key: 'corners', to: '/guide/corners', icon: MapPin, label: 'Les corners', desc: 'Presentation de chaque corner', color: 'bg-orange-50 text-orange-600 border-orange-100' },
    { key: 'recettes', to: '/guide/recettes', icon: BookOpen, label: 'Recettes', desc: 'Fiches recettes et couts de revient', color: 'bg-amber-50 text-amber-700 border-amber-100' },
    { key: 'haccp', to: '/guide/haccp', icon: ShieldCheck, label: 'HACCP', desc: 'Regles HACCP generales', color: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
    { key: 'haccp-stand', to: '/guide/haccp/stand', icon: Monitor, label: 'HACCP Stand', desc: 'Obligations HACCP stand — Epack Pro', color: 'bg-teal-50 text-teal-600 border-teal-100' },
    { key: 'arrivee', to: '/guide/checklist/arrivee', icon: ClipboardCheck, label: "Debut de service", desc: "Guide etape par etape", color: 'bg-blue-50 text-blue-600 border-blue-100' },
    { key: 'depart', to: '/guide/checklist/depart', icon: ClipboardList, label: 'Fiche de depart', desc: 'Check-list de fermeture du stand', color: 'bg-indigo-50 text-indigo-600 border-indigo-100' },
];

export default function GuideHomePage() {
    const user = JSON.parse(localStorage.getItem('guide_user') || '{}');
    const isAdmin = user.role === 'admin';
    const [descs, setDescs] = useState({});
    const [editing, setEditing] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await authFetch(`${API}/api/guide/pages/home`);
                if (res.ok) {
                    const data = await res.json();
                    setDescs(data?.descriptions || {});
                }
            } catch {}
        };
        load();
    }, []);

    const startEdit = (key, currentDesc) => {
        setEditing(key);
        setEditValue(currentDesc);
    };

    const saveDesc = async () => {
        setSaving(true);
        try {
            const newDescs = { ...descs, [editing]: editValue };
            await authFetch(`${API}/api/guide/pages/home`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ descriptions: newDescs }),
            });
            setDescs(newDescs);
            setEditing(null);
            toast.success('Description mise a jour');
        } catch { toast.error('Erreur'); }
        finally { setSaving(false); }
    };

    return (
        <div className="px-4 py-5" data-testid="guide-home">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-slate-900">
                    Bienvenue{user.name ? `, ${user.name.split(' ')[0]}` : ''} !
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">Guide du Sushiman Fuji Express</p>
            </div>

            <div className="grid grid-cols-1 gap-3">
                {defaultMenuItems.map(item => {
                    const desc = descs[item.key] || item.desc;
                    const isEditing = editing === item.key;

                    return (
                        <div key={item.key} className="relative">
                            <Link
                                to={item.to}
                                className="flex items-center gap-4 p-4 rounded-xl border bg-white shadow-sm active:scale-[0.98] transition-transform"
                                data-testid={`guide-menu-${item.label.toLowerCase().replace(/[' ]/g, '-')}`}
                                onClick={e => { if (isEditing) e.preventDefault(); }}
                            >
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                                    <item.icon className="w-6 h-6" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-slate-900 text-sm">{item.label}</p>
                                    {isEditing ? (
                                        <div className="mt-1" onClick={e => e.preventDefault()}>
                                            <input
                                                value={editValue}
                                                onChange={e => setEditValue(e.target.value)}
                                                className="w-full border border-slate-300 rounded px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
                                                autoFocus
                                                onKeyDown={e => { if (e.key === 'Enter') saveDesc(); if (e.key === 'Escape') setEditing(null); }}
                                                data-testid={`edit-desc-input-${item.key}`}
                                            />
                                            <div className="flex gap-2 mt-1">
                                                <button onClick={(e) => { e.preventDefault(); setEditing(null); }} className="flex items-center gap-0.5 text-[10px] text-slate-500">
                                                    <X className="w-3 h-3" /> Annuler
                                                </button>
                                                <button onClick={(e) => { e.preventDefault(); saveDesc(); }} disabled={saving} className="flex items-center gap-0.5 text-[10px] text-blue-600 font-medium">
                                                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Enregistrer
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                                    )}
                                </div>
                            </Link>
                            {isAdmin && !isEditing && (
                                <button
                                    onClick={() => startEdit(item.key, desc)}
                                    className="absolute top-2 right-2 p-1.5 text-slate-300 hover:text-slate-500"
                                    data-testid={`edit-desc-btn-${item.key}`}
                                >
                                    <Edit3 className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
